import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RiCoupon3Line,
  RiWallet3Line,
  RiQuestionnaireLine,
  RiArrowRightSLine,
  RiArrowDownSLine,
  RiArrowRightLine,
} from "react-icons/ri";
import api from "@services/axios/apiManager";

const C = {
  ink: "#1f2937",
  muted: "#8b909c",
  blue: "#2196f3",
  blueTint: "#e3f2fd",
  blueText: "#1976d2",
  page: "#f8fafc",
  cardShadow: "0 3px 10px -8px rgba(31,41,55,0.18)",
};

interface TopCoupon {
  amount: number;
  type: string;
  name: string;
  expire: string | null;
  minSpend: number;
}

interface CouponApiItem {
  status?: string;
  coupon_name?: string;
  coupon_type?: string;
  discount_value?: number;
  min_order_amount?: number;
  expire_time_stamp?: string | null;
}

interface InfoData {
  availableCoupons: number;
  topCoupon: TopCoupon | null;
  loading: boolean;
}

const EMPTY: InfoData = { availableCoupons: 0, topCoupon: null, loading: true };

/** 抓優惠券（張數＋面額最高的一張），供帳戶摘要 + 優惠券票券共用。 */
const useHomeInfoData = (token?: string | null, xContext?: string | null): InfoData => {
  const [data, setData] = useState<InfoData>(EMPTY);

  useEffect(() => {
    let alive = true;
    if (!token || !xContext) {
      setData({ ...EMPTY, loading: false });
      return;
    }

    const run = async () => {
      let availableCoupons = 0;
      let topCoupon: TopCoupon | null = null;

      // 優惠券：/coupons 取 status=available，計數 + 挑面額最高的一張做票券主秀
      try {
        const res = await api.get("/app-api/coupons", {
          headers: { Authorization: `Bearer ${token}`, "X-Context": xContext },
        });
        const items = (res.data?.data?.items ?? []) as CouponApiItem[];
        const avail = items.filter((it) => it.status === "available");
        availableCoupons = avail.length;
        if (avail.length) {
          const best = avail.reduce((a, b) => ((b.discount_value ?? 0) > (a.discount_value ?? 0) ? b : a));
          topCoupon = {
            amount: best.discount_value ?? 0,
            type: best.coupon_type ?? "",
            name: best.coupon_name ?? "優惠券",
            expire: best.expire_time_stamp ?? null,
            minSpend: best.min_order_amount ?? 0,
          };
        }
      } catch (err) {
        console.warn("home coupons fetch failed", err);
      }

      if (alive) setData({ availableCoupons, topCoupon, loading: false });
    };

    setData((d) => ({ ...d, loading: true }));
    run();
    return () => {
      alive = false;
    };
  }, [token, xContext]);

  return data;
};

/** 折抵顯示：百分比券顯示 N%，定額券顯示 $N */
const couponBigText = (c: TopCoupon): string =>
  /percent|percentage|rate/i.test(c.type) ? `${c.amount}%` : `$${Math.round(c.amount)}`;

/** 期限 → M/D（解析失敗則不顯示） */
const couponExpiry = (ts: string | null): string | null => {
  if (!ts) return null;
  const d = new Date(ts.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/** 票券中央的垂直鋸齒撕線路徑（viewBox 0 0 8 64，preserveAspectRatio=none 拉伸貼合高度） */
const ZIGZAG_PATH = (() => {
  const teeth = 8;
  const step = 64 / teeth;
  const pts: string[] = [];
  for (let i = 0; i <= teeth; i++) pts.push(`${i % 2 === 0 ? 1.6 : 6.4},${(step * i).toFixed(1)}`);
  return "M" + pts.join(" L");
})();

/**
 * 我的帳戶摘要 + 優惠券卡（有可用券才顯示優惠券卡）。
 * 綁定客戶才會抓資料；未綁定 / 無 context 時不顯示。
 * 首頁不放「待付金額」：要看欠款到帳戶頁（Account 的摘要仍有這格）。
 */
export const AccountCouponCards = ({
  token,
  xContext,
}: {
  token?: string | null;
  xContext?: string | null;
}) => {
  const navigate = useNavigate();
  const { availableCoupons, topCoupon } = useHomeInfoData(token, xContext);

  if (!token || !xContext) return null;

  const expiry = topCoupon ? couponExpiry(topCoupon.expire) : null;

  // TODO(存氣折扣): 後端尚無「存氣折扣 / 儲值餘額」端點，暫不顯示此欄。
  // 待 API 就緒（例如 /app-api/customer/wallet 或 home 回傳 stored_gas_discount）再加回第三格。
  const cells: { icon: JSX.Element; label: string; value: string; todo?: boolean }[] = [
    { icon: <RiCoupon3Line size={20} color={C.blueText} />, label: "優惠券", value: `${availableCoupons} 張` },
  ];

  return (
    <>
      {availableCoupons > 0 && topCoupon && (
        <button
          type="button"
          onClick={() => navigate("/account/coupons")}
          style={{
            width: "100%",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
            borderRadius: 16,
            padding: "14px 16px",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 14,
            boxShadow: C.cardShadow,
            position: "relative",
            overflow: "hidden",
          }}>
          <style>{`
            @keyframes couponShine { 0% { transform: translateX(-120%); } 55%, 100% { transform: translateX(320%); } }
          `}</style>
          {/* 光掃（淡橘） */}
          <span
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "40%",
              height: "100%",
              background: "linear-gradient(100deg,transparent,rgba(249,115,22,0.10),transparent)",
              animation: "couponShine 3.6s ease-in-out infinite",
              pointerEvents: "none",
            }}
          />
          {/* 左右票根缺口（與白卡區底色同色）*/}
          <span style={{ position: "absolute", top: "50%", left: -8, width: 16, height: 16, borderRadius: "50%", background: C.page, transform: "translateY(-50%)" }} />
          <span style={{ position: "absolute", top: "50%", right: -8, width: 16, height: 16, borderRadius: "50%", background: C.page, transform: "translateY(-50%)" }} />

          {/* 左：折抵面額（大）*/}
          <span style={{ flexShrink: 0, textAlign: "center", position: "relative" }}>
            <span style={{ display: "block", fontSize: 11, color: "#F97316", fontWeight: 700 }}>折抵</span>
            <span style={{ display: "block", fontSize: 26, fontWeight: 900, color: "#C2410C", lineHeight: 1.05, fontVariantNumeric: "tabular-nums" }}>
              {couponBigText(topCoupon)}
            </span>
          </span>

          {/* 中間鋸齒撕線 */}
          <svg
            viewBox="0 0 8 64"
            preserveAspectRatio="none"
            aria-hidden="true"
            style={{ width: 8, height: 46, flexShrink: 0, position: "relative" }}>
            <path d={ZIGZAG_PATH} fill="none" stroke="#EDA45E" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
          </svg>

          {/* 右：名稱 + 期限/張數 */}
          <span style={{ flex: 1, minWidth: 0, position: "relative" }}>
            <span
              style={{
                display: "block",
                fontSize: 13.5,
                fontWeight: 700,
                color: C.ink,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
              {topCoupon.name}
            </span>
            <span style={{ display: "block", fontSize: 11.5, color: C.muted, marginTop: 2 }}>
              {expiry ? `${expiry} 前使用` : "叫桶時可折抵"}
              {availableCoupons > 1 ? ` · 共 ${availableCoupons} 張` : ""}
            </span>
          </span>

          {/* 使用膠囊 */}
          <span
            style={{
              flexShrink: 0,
              position: "relative",
              fontSize: 13,
              fontWeight: 800,
              color: "#fff",
              background: "#F97316",
              borderRadius: 10,
              padding: "8px 13px",
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
            }}>
            使用
            <RiArrowRightLine size={15} color="#fff" />
          </span>
        </button>
      )}

      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            margin: "0 0 10px 4px",
          }}>
          <RiWallet3Line size={16} color={C.muted} />
          <span style={{ fontSize: 14, fontWeight: 500, color: C.muted }}>我的帳戶</span>
          <button
            type="button"
            onClick={() => navigate("/account")}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "none",
              color: C.muted,
              fontSize: 12,
              cursor: "pointer",
              fontFamily: "inherit",
              display: "inline-flex",
              alignItems: "center",
            }}>
            查看全部
            <RiArrowRightSLine size={15} color={C.muted} />
          </button>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {cells.map((c) => (
            <button
              key={c.label}
              type="button"
              onClick={() => navigate(c.label === "優惠券" ? "/account/coupons" : "/account")}
              style={{
                flex: 1,
                minWidth: 0,
                position: "relative",
                background: "#fff",
                borderRadius: 999,
                padding: "10px 14px",
                boxShadow: C.cardShadow,
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}>
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: C.blueTint,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}>
                {c.icon}
              </span>
              <span style={{ minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontSize: 15,
                    fontWeight: 800,
                    color: C.ink,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.2,
                  }}>
                  {c.value}
                </span>
                <span style={{ display: "block", fontSize: 10.5, color: C.muted, marginTop: 1 }}>{c.label}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

/**
 * 第一題的答案依瓦斯行有沒有開 AI 叫桶預測而不同——關閉的瓦斯行不能在這裡
 * 承諾「智慧預測會主動提醒您」，那是他們拿不到的功能。
 */
const faqItems = (aiForecastEnabled: boolean) => [
  {
    title: "怎麼知道瓦斯快用完？",
    content: aiForecastEnabled
      ? "首頁的智慧預測會依您的叫桶紀錄，估算瓦斯大約在哪段期間用完，並在接近時主動提醒您。"
      : "首頁會顯示您平均多久叫一次瓦斯，可以依這個週期估算下次叫桶的時間。",
  },
  {
    title: "配送要多久會到？",
    content: "送達時間依瓦斯行與地區而定，通常當天或隔日；下單後可在訂單頁追蹤進度。",
  },
  {
    title: "可以指定送達時間嗎？",
    content: "下單時可選擇希望的配送時段，實際到府時間以瓦斯行安排為準。",
  },
];

/** 常見問題手風琴卡（純前端，點擊展開／收合答案；單開） */
export const FaqCard = ({ aiForecastEnabled = true }: { aiForecastEnabled?: boolean }) => {
  const [open, setOpen] = useState<number | null>(null);
  const FAQ_ITEMS = faqItems(aiForecastEnabled);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "0 0 10px 4px" }}>
        <RiQuestionnaireLine size={16} color={C.muted} />
        <span style={{ fontSize: 14, fontWeight: 500, color: C.muted }}>常見問題</span>
      </div>
      <div style={{ background: "#fff", borderRadius: 16, padding: "2px 14px", boxShadow: C.cardShadow }}>
        {FAQ_ITEMS.map((q, i) => {
          const isOpen = open === i;
          return (
            <div key={q.title} style={{ borderTop: i > 0 ? "1px solid #F1F2F4" : "none" }}>
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : i)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "13px 0",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: C.ink }}>
                  {q.title}
                </span>
                <RiArrowDownSLine
                  size={18}
                  color={isOpen ? C.blueText : "#CBD5E1"}
                  style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.25s ease", flexShrink: 0 }}
                />
              </button>
              <div
                style={{
                  display: "grid",
                  gridTemplateRows: isOpen ? "1fr" : "0fr",
                  transition: "grid-template-rows 0.28s ease",
                }}>
                <div style={{ overflow: "hidden" }}>
                  <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, padding: "0 0 13px" }}>{q.content}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
